import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, KeyRound, LoaderCircle, Mail } from 'lucide-react';
import { Field, Input } from '@ampm-7en-ai/ui/components/Input';
import { Button } from '@ampm-7en-ai/ui/components/Button';
import { Callout } from '@ampm-7en-ai/ui/components/Callout';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { getApiUrl, API_ENDPOINTS, authApi } from '@/utils/api-config';
import { GOOGLE_AUTH_CONFIG, GOOGLE_OAUTH_SCOPES, RECAPTCHA_CONFIG } from '@/utils/auth-config';
import { DEFAULT_DASHBOARD_PATH } from '@/lib/uiSwitch';

const otpSchema = z.object({
  otp: z.string().regex(/^\d{6}$/, "Enter the 6-digit login code")
});

const otpLoginSchema = z.object({
  email: z.string().trim().email("Enter a valid email address"),
});

type OtpFormValues = z.infer<typeof otpSchema>;
type OtpLoginFormValues = z.infer<typeof otpLoginSchema>;

interface LoginFormProps {
  onOtpVerificationNeeded: (email: string) => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onOtpVerificationNeeded }) => {
  const [currentEmail, setCurrentEmail] = useState('');
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const [showOtpVerification, setShowOtpVerification] = useState(false);
  const [otpEmail, setOtpEmail] = useState('');
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isResendingOtp, setIsResendingOtp] = useState(false);
  const [flowError, setFlowError] = useState<{ title: string; description: string } | null>(null);
  const [resendNotice, setResendNotice] = useState<string | null>(null);
  const { login, setPendingVerificationEmail, setNeedsVerification } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const otpForm = useForm<OtpFormValues>({
    resolver: zodResolver(otpSchema),
    defaultValues: {
      otp: ''
    }
  });

  const emailForm = useForm<OtpLoginFormValues>({
    resolver: zodResolver(otpLoginSchema),
    defaultValues: {
      email: '',
    }
  });

  const emailValue = emailForm.watch('email');
  const isEmailReady = Boolean(emailValue?.trim());
  const otpBusy = isVerifyingOtp || isResendingOtp;

  const reportError = (title: string, description: string) => {
    setFlowError({ title, description });
  };

  const handleEmailSubmit = async (values: OtpLoginFormValues) => {
    await handleSendOtpCode(values.email);
  };

  const handleSendOtpCode = async (emailOverride?: string) => {
    const rawEmail = emailOverride ?? emailForm.getValues('email') ?? currentEmail;
    const candidateEmail = rawEmail?.trim() ?? '';

    if (!candidateEmail) {
      reportError('Email required', 'Please enter your email first.');
      return;
    }

    setCurrentEmail(candidateEmail);
    setFlowError(null);
    setResendNotice(null);
    setIsSendingOtp(true);

    try {
      // Execute reCAPTCHA
      let recaptchaToken: string | undefined;
      try {
        recaptchaToken = await window.grecaptcha.execute(RECAPTCHA_CONFIG.SITE_KEY, { action: 'code_login' });
      } catch (error) {
        console.error('reCAPTCHA error:', error);
      }

      const response = await authApi.codeLogin(candidateEmail, recaptchaToken);

      if (response.ok) {
        const data = await response.json();
        setOtpEmail(candidateEmail);
        setShowOtpVerification(true);
        otpForm.reset();
        onOtpVerificationNeeded(candidateEmail);

        toast({
          title: "Login code sent",
          description: data.message || "Login code sent to your email.",
          variant: "default",
        });
      } else {
        const errorData = await response.json();
        reportError('Could not send the code', errorData.message || 'Please try again.');
      }
    } catch (error) {
      console.error("Send OTP error:", error);
      reportError('Could not send the code', 'Please try again later.');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtpCode = async (values: { otp: string }) => {
    setFlowError(null);
    setResendNotice(null);
    setIsVerifyingOtp(true);
    
    try {
      // Execute reCAPTCHA
      let recaptchaToken: string | undefined;
      try {
        recaptchaToken = await window.grecaptcha.execute(RECAPTCHA_CONFIG.SITE_KEY, { action: 'verify_code' });
      } catch (error) {
        console.error('reCAPTCHA error:', error);
      }
      
      const response = await authApi.verifyCodeLogin(otpEmail, values.otp, recaptchaToken);
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.data && data.data.userData) {
          // Check verification status
          if (!data.data.userData.is_verified) {
            setPendingVerificationEmail(otpEmail);
            setNeedsVerification(true);
            
            toast({
              title: "Account Not Verified",
              description: data.message || "Please verify your account first",
              variant: "destructive",
            });
            
            navigate('/verify', { state: { email: otpEmail } });
            return;
          }
          
          // User is verified, proceed with login
          //const userRole = data.data.userData.user_role === "admin" ? "admin" : data.data.userData.user_role;
          
          await login(data.data.userData.username, "", {
            access: data.data.access,
            refresh: data.data.refresh || null,
            user_id: data.data.user_id,
            userData: {
              username: data.data.userData.username,
              email: data.data.userData.email || otpEmail,
              avatar: data.data.userData.avatar,
              team_role: data.data.userData.team_role || null,
              user_role: "SUPERADMIN",
              permissions: data.data.userData.permissions || {},
              is_verified: true
            }
          });
          
          toast({
            title: "Login successful",
            description: "Welcome!",
            variant: "default",
          });
          
          
          navigate(DEFAULT_DASHBOARD_PATH);
          
        } else {
          console.error('Login response did not include user data');
          reportError('Could not sign in', 'The server returned an incomplete login response. Please try again.');
        }
      } else {
        const errorData = await response.json();
        reportError('Invalid code', errorData.message || 'The login code is invalid or expired.');
      }
    } catch (error) {
      console.error("Verify OTP error:", error);
      reportError('Could not verify the code', 'Please try again later.');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const resetEmailFlow = () => {
    setCurrentEmail('');
    setShowOtpVerification(false);
    setOtpEmail('');
    emailForm.reset();
    otpForm.reset();
    setFlowError(null);
    setResendNotice(null);
  };

  const handleResendOtpCode = async () => {
    const targetEmail = (otpEmail || currentEmail || emailForm.getValues('email') || '').trim();

    if (!targetEmail) {
      reportError('Email required', 'Please enter your email first.');
      return;
    }

    setFlowError(null);
    setResendNotice(null);
    setIsResendingOtp(true);

    try {
      let recaptchaToken: string | undefined;
      try {
        recaptchaToken = await window.grecaptcha.execute(RECAPTCHA_CONFIG.SITE_KEY, { action: 'resend_code' });
      } catch (error) {
        console.error('reCAPTCHA error:', error);
      }

      const response = await authApi.resendCodeLogin(targetEmail, recaptchaToken);
      const data = await response.json();

      if (response.ok) {
        setResendNotice('A new code has been sent. Check your inbox and spam folder.');
        toast({
          title: "Code resent",
          description: data.message || "OTP resent to superadmin email.",
          variant: "default",
        });
      } else {
        reportError('Could not resend the code', data.error?.message || 'Please try again.');
      }
    } catch (error) {
      console.error('Resend OTP error:', error);
      reportError('Could not resend the code', 'Please try again later.');
    } finally {
      setIsResendingOtp(false);
    }
  };

  const handleSSOLogin = async (provider: 'google' | 'apple', token: string) => {
    try {
      const formData = new FormData();
      formData.append('sso_token', token);
      formData.append('provider', provider);
      
      console.log(`Sending ${provider.toUpperCase()} SSO request to endpoint:`, API_ENDPOINTS.SSO_LOGIN);
      
      const apiUrl = getApiUrl(API_ENDPOINTS.SSO_LOGIN+"?provider="+provider);
      console.log("Full API URL:", apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formData,
      });
      
      console.log(`${provider.toUpperCase()} SSO login response status:`, response.status);
      
      const data = await response.json();
      console.log(`${provider.toUpperCase()} SSO login response:`, data);
      
      if (response.ok && data.data.access) {
        const userRole = data.data.userData.user_role === "admin" ? "admin" : data.data.userData.user_role;
        
        await login(data.data.userData.username || `${provider} User`, "", {
          access: data.data.access,
          refresh: data.refresh || null,
          user_id: data.data.user_id,
          subscription: {
            subscription_failed: data.data.subscription ? data.data.subscription.subscription_failed : false,
          },
          userData: {
            username: data.data.userData.username,
            email: data.data.userData.email || `${provider}_user@example.com`,
            avatar: data.data.userData.avatar,
            team_role: data.data.userData.team_role || null,
            user_role: userRole,
            permissions: data.data.userData.permissions || {},
            is_verified: true
          }
        });
        
        toast({
          title: "Login successful",
          description: "Welcome back!",
          variant: "default",
        });
        
        if (userRole === 'superadmin') {
          navigate(DEFAULT_DASHBOARD_PATH);
        } else if (userRole === 'admin') {
          navigate('/dashboard/admin');
        } else {
          navigate(DEFAULT_DASHBOARD_PATH);
        }
      } else if (data.error) {
        toast({
          title: "Login Failed",
          description: data.error,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Login Failed",
          description: "An unexpected error occurred. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(`${provider.toUpperCase()} SSO login error:`, error);
      toast({
        title: "Login Failed",
        description: `Could not complete ${provider} sign-in. Please try again later.`,
        variant: "destructive",
      });
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setIsGoogleLoading(true);
      
      const googleAuth = window.google?.accounts?.oauth2;
      
      if (!googleAuth) {
        toast({
          title: "Google Sign-In Error",
          description: "Google authentication is not available. Please try again later.",
          variant: "destructive",
        });
        setIsGoogleLoading(false);
        return;
      }
      
      const client = googleAuth.initTokenClient({
        client_id: GOOGLE_AUTH_CONFIG.CLIENT_ID,
        scope: GOOGLE_OAUTH_SCOPES,
        redirect_uris: GOOGLE_AUTH_CONFIG.REDIRECT_URI,
        callback: async (tokenResponse) => {
          console.log("Google OAuth token response:", tokenResponse);
          
          if (tokenResponse.error || !tokenResponse.access_token) {
            console.error('Google Sign-In error:', tokenResponse.error);
            if (tokenResponse.error !== 'popup_closed_by_user') {
              toast({
                title: "Google Sign-In Failed", 
                description: "There was an error signing in with Google. Please try again.",
                variant: "destructive",
              });
            }
            setIsGoogleLoading(false);
            return;
          }
          
          await handleSSOLogin('google', tokenResponse.access_token);
          setIsGoogleLoading(false);
        },
        error_callback: () => {
          setIsGoogleLoading(false);
        }
      });
      
      client.requestAccessToken();
      
    } catch (error) {
      console.error("Google Sign-In initialization error:", error);
      toast({
        title: "Google Sign-In Error",
        description: "Could not initialize Google Sign-In. Please try again later.",
        variant: "destructive",
      });
      setIsGoogleLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    try {
      setIsAppleLoading(true);
      
      const AppleID = window.AppleID;
      
      if (!AppleID) {
        toast({
          title: "Apple Sign-In Error",
          description: "Apple authentication is not available. Please try again later.",
          variant: "destructive",
        });
        setIsAppleLoading(false);
        return;
      }
      
      AppleID.auth.init({
        clientId: 'com.ampmlabs.7enai.signin',
        scope: 'name email',
        redirectURI: window.location.origin+"/dashboard",
        usePopup: true
      });
      
      const data = await AppleID.auth.signIn();
      console.log("Apple OAuth response:", data);
      
      if (data.authorization && data.authorization.id_token) {
        await handleSSOLogin('apple', data.authorization.id_token);
      } else {
        toast({
          title: "Apple Sign-In Failed",
          description: "Could not get authentication token from Apple.",
          variant: "destructive",
        });
      }
      
      setIsAppleLoading(false);
      
    } catch (error) {
      console.error("Apple Sign-In error:", error);
      
      // Don't show error for user cancellation
      if (!(typeof error === 'object' && error !== null && 'error' in error && error.error === 'popup_closed_by_user')) {
        toast({
          title: "Apple Sign-In Error",
          description: "Could not complete Apple sign-in. Please try again later.",
          variant: "destructive",
        });
      }
      
      setIsAppleLoading(false);
    }
  };

  useEffect(() => {
    const source = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_CONFIG.SITE_KEY}`;
    if (Array.from(document.scripts).some((script) => script.src === source)) return;
    const recaptchaScript = document.createElement('script');
    recaptchaScript.src = source;
    recaptchaScript.async = true;
    document.head.appendChild(recaptchaScript);
  }, []);

  return (
    <div>
      {showOtpVerification ? (
        <>
          <Button variant="ghost" size="sm" className="-ml-3 mb-5" onClick={resetEmailFlow} disabled={otpBusy}>
            <ArrowLeft aria-hidden />Use a different email
          </Button>
          <div className="mb-6">
            <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-ui-md border border-ui-border bg-ui-subtle">
              <Mail className="h-5 w-5" aria-hidden />
            </div>
            <h1 className="text-ui-3xl font-semibold tracking-tight">Check your email</h1>
            <p className="mt-2 text-ui-base leading-relaxed text-ui-fg-muted">
              Enter the 6-digit login code sent to <strong className="break-all font-medium text-ui-fg">{otpEmail}</strong>.
            </p>
          </div>
          {flowError && <Callout tone="danger" title={flowError.title} className="mb-5">{flowError.description}</Callout>}
          {resendNotice && <Callout tone="success" className="mb-5">{resendNotice}</Callout>}
          <form noValidate onSubmit={otpForm.handleSubmit(handleVerifyOtpCode)} aria-busy={otpBusy}>
            <fieldset disabled={otpBusy} className="min-w-0 space-y-5">
              <Field label="Login code" htmlFor="login-code" hint="Paste the code from your email." error={otpForm.formState.errors.otp?.message}>
                <Input
                  {...otpForm.register('otp')}
                  id="login-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={6}
                  placeholder="000000"
                  className="h-12 text-center font-ui-mono text-xl tracking-[0.35em]"
                  onChange={(event) => otpForm.setValue('otp', event.target.value.replace(/\D/g, '').slice(0, 6), { shouldValidate: otpForm.formState.isSubmitted })}
                />
              </Field>
              <Button type="submit" variant="primary" className="h-11 w-full">
                {isVerifyingOtp ? <><LoaderCircle className="animate-spin motion-reduce:animate-none" aria-hidden />Signing in...</> : <>Sign in<ArrowRight aria-hidden /></>}
              </Button>
            </fieldset>
          </form>
          <div className="mt-5 text-center">
            <p className="text-ui-xs text-ui-fg-muted">No code yet? Check your spam folder, or</p>
            <Button variant="ghost" size="sm" onClick={handleResendOtpCode} disabled={otpBusy} className="mt-1">
              {isResendingOtp ? <><LoaderCircle className="animate-spin motion-reduce:animate-none" aria-hidden />Sending a new code...</> : 'Resend code'}
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="mb-6">
            <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-ui-md border border-ui-border bg-ui-subtle">
              <KeyRound className="h-5 w-5" aria-hidden />
            </div>
            <h1 className="text-ui-3xl font-semibold tracking-tight">Sign in to superadmin</h1>
            <p id="login-description" className="mt-2 text-ui-base leading-relaxed text-ui-fg-muted">
              Use your authorized email. We will send you a one-time login code.
            </p>
          </div>
          {flowError && <Callout tone="danger" title={flowError.title} className="mb-5">{flowError.description}</Callout>}
          <form noValidate onSubmit={emailForm.handleSubmit(handleEmailSubmit)} aria-describedby="login-description" aria-busy={isSendingOtp}>
            <fieldset disabled={isSendingOtp} className="min-w-0 space-y-5">
              <Field label="Email address" htmlFor="login-email" error={emailForm.formState.errors.email?.message}>
                <Input
                  {...emailForm.register('email')}
                  id="login-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  autoFocus
                  placeholder="name@company.com"
                  className="h-11 text-base sm:text-ui-base"
                />
              </Field>
              <Button type="submit" variant="primary" className="h-11 w-full" disabled={!isEmailReady}>
                {isSendingOtp ? <><LoaderCircle className="animate-spin motion-reduce:animate-none" aria-hidden />Sending login code...</> : <>Send login code<ArrowRight aria-hidden /></>}
              </Button>
            </fieldset>
          </form>
          <p className="mt-4 text-center text-ui-xs text-ui-fg-muted">No password required.</p>
        </>
      )}
    </div>
  );
};

export default LoginForm;
